import React from 'react';
import withShipment from '../../withShipment';
import CustomToolbar from '../global/CustomToolbar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import { userInfoSelector } from '../../redux/selectors/auth';
import { auditLogsSelector } from '../../redux/selectors/audit';
import { listReadingSessionAudit } from '../../redux/actions/audit';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';

const ACTION_COLOR = {
  REOPEN_SESSION: 'warning',
  ASSIGN_MACHINE_CREATE: 'success',
  ASSIGN_MACHINE_UPDATE: 'info',
  ASSIGN_MACHINE_DELETE: 'error',
  READING_EDIT_REOPENED: 'warning',
  ASSIGN_MACHINE_LATEST_READING_REWRITE: 'error',
};

class AuditRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
  }

  isIsoDateString(v) {
    return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v);
  }

  formatValue(v) {
    if (v === null || v === undefined || v === '') return '(none)';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (v instanceof Date) return this.formatDate(v);
    if (this.isIsoDateString(v)) return this.formatDate(v);
    if (typeof v === 'object') {
      // Mongo decimals come through as { $numberDecimal: "123" } — unwrap
      if (v.$numberDecimal !== undefined) return String(v.$numberDecimal);
      if (v.$date !== undefined) return this.formatDate(v.$date);
      return JSON.stringify(v);
    }
    return String(v);
  }

  formatDate(d) {
    if (!d) return '(none)';
    try {
      const date = new Date(d);
      // e.g. "Sat, Apr 11, 2026"
      const datePart = date.toLocaleDateString('en-US', {
        timeZone: 'America/Chicago',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      // e.g. "4:59:32 PM CT"
      const timePart = date.toLocaleTimeString('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
      return `${datePart} · ${timePart}`;
    } catch (e) {
      return String(d);
    }
  }

  formatFieldName(f) {
    const map = {
      ended: 'Ended',
      endedAt: 'Ended At',
      reopenedAt: 'Reopened At',
      prevIn: 'Prev In',
      prevOut: 'Prev Out',
      curIn: 'Current In',
      curOut: 'Current Out',
      inValue: 'Daily In',
      outValue: 'Daily Out',
      difference: 'Difference',
      lifePay: 'Life Pay %',
      lifeHold: 'Life Hold %',
      isReset: 'Is Reset',
      machine: 'Machine',
      type: 'Type',
    };
    return map[f] || f;
  }

  render() {
    const { row } = this.props;
    const { open } = this.state;
    const changedFields = row.changedFields || [];
    return (
      <React.Fragment>
        <TableRow hover>
          <TableCell style={{ width: 40 }}>
            <IconButton size="small" onClick={() => this.setState({ open: !open })}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{this.formatDate(row.changedAt)}</TableCell>
          <TableCell>{row.userName || '—'}</TableCell>
          <TableCell>
            <Chip
              size="small"
              label={row.action}
              color={ACTION_COLOR[row.action] || 'default'}
            />
          </TableCell>
          <TableCell>{row.entityType}</TableCell>
          <TableCell>{row.readingSessionId || '—'}</TableCell>
          <TableCell>{row.machine || '—'}</TableCell>
          <TableCell>{changedFields.map((f) => this.formatFieldName(f)).join(', ') || '(none)'}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Changes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Field</strong></TableCell>
                      <TableCell><strong>Old Value</strong></TableCell>
                      <TableCell><strong>New Value</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changedFields.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <em>No field-level diff recorded.</em>
                        </TableCell>
                      </TableRow>
                    )}
                    {changedFields.map((f) => (
                      <TableRow key={f}>
                        <TableCell>{this.formatFieldName(f)}</TableCell>
                        <TableCell style={{ color: '#b71c1c' }}>
                          {this.formatValue(row.oldValues && row.oldValues[f])}
                        </TableCell>
                        <TableCell style={{ color: '#1b5e20' }}>
                          {this.formatValue(row.newValues && row.newValues[f])}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
}

class ReadingSessionAuditLog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      feedbackBarOpen: false,
      feedbackMessage: '',
      feedbackStatus: '',
    };
  }

  componentDidMount() {
    this.fetch();
  }

  fetch = () => {
    this.props.listReadingSessionAudit({
      locationId: this.props.userInfo.selectedLocationId,
      readingSessionId: this.props.embeddedSessionId || null,
      entityType: null,
    });
  };

  closeFeedbackBar = () => {
    this.setState({ feedbackBarOpen: false });
  };

  render() {
    const logs = this.props.logs || [];
    const isEmbedded = !!this.props.onBack;
    const titleSuffix = this.props.embeddedSessionId
      ? ` — Session #${this.props.embeddedSessionId}`
      : '';

    const headerButtons = [];
    if (isEmbedded) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: this.props.onBack,
        title: 'Back',
      });
    }
    headerButtons.push({
      icon: <RefreshIcon />,
      handler: this.fetch,
      title: 'Refresh',
    });

    return (
      <React.Fragment>
        <CustomToolbar
          title={`Reading Session Audit Log${titleSuffix}`}
          createTitle={''}
          backTitle={''}
          headerButtons={headerButtons}
        />
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        <TableContainer component={Paper} sx={{ margin: 2, width: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell><strong>When</strong></TableCell>
                <TableCell><strong>Who</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
                <TableCell><strong>Entity</strong></TableCell>
                <TableCell><strong>Session #</strong></TableCell>
                <TableCell><strong>Machine</strong></TableCell>
                <TableCell><strong>Changed Fields</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <em>No audit entries.</em>
                  </TableCell>
                </TableRow>
              )}
              {logs.map((row) => (
                <AuditRow key={row.auditId} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  logs: auditLogsSelector(state),
});

const actionCreators = {
  listReadingSessionAudit,
};

export default withShipment({ mapStateToProps, actionCreators }, ReadingSessionAuditLog);


